// Imports
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import NewCommunity from './new-community';
import Finish from './finish';
import MultiStepErrorHandler from './multi-step-error-handler';
import APIForm, { APIFormOptions } from '../../shareables/integrations/api/components/form';
import { GetStartResponse } from '../../shareables/types/api/main/start/get';
import SignUp from './sign-up';
import VerificationCode from './verification-code';
import styled from 'styled-components/macro';
import User from './user';
import Location from './location';
import { StepNumbers } from '../app';
import MigrateCommunity from './migrate-community';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import * as Sentry from '@sentry/browser';
import Select from '../../shareables/foundation/front-end/components/forms/fields/select';
import { faCommentQuestion } from '@fortawesome/pro-solid-svg-icons';
import { fieldContainerBottomMargin } from '../../shareables/foundation/front-end/components/forms/field-container';


// Styled components
const SelectWrapper = styled.div`
	margin-bottom: ${fieldContainerBottomMargin};
	
	& label small {
		white-space: nowrap;
	}
	
	& label small::before {
		content: '⁠';
	}
`;

const Subtext = styled.div`
	text-align: center;
	font-size: 0.72rem;
	font-style: normal;
	font-weight: 300;
	line-height: 1;
`;


// Define the accepted props
interface Props {
	startState: GetStartResponse;
	memoizedLoadStart: () => Promise<void>;
	memoizedLoadSelf: () => Promise<void>;
	stepNumber: StepNumbers;
	setStepNumber: React.Dispatch<React.SetStateAction<StepNumbers>>;
	migrate: boolean;
	storybookEmail?: string;
	storybookCommunityName?: string;
	storybookOnboardingCallScheduled?: boolean;
	storybookLoadingCompletion?: boolean;
	storybookCompleted?: boolean;
	storybookContainsProtectedContent?: boolean;
}


// Function component
const Steps: React.FC<Props> = ({
	stepNumber,
	setStepNumber,
	startState,
	memoizedLoadStart,
	memoizedLoadSelf,
	migrate,
	storybookEmail,
	storybookCommunityName,
	storybookOnboardingCallScheduled,
	storybookLoadingCompletion,
	storybookCompleted,
	storybookContainsProtectedContent,
}) => {
	// Use state
	const [verificationCodeExpiredError, setVerificationCodeExpiredError] = useState('');
	const [fingerprint, setFingerprint] = useState<string | null>(null);
	
	
	// Use ref
	const emailGetter = useRef<() => string>();
	const verificationCodeSetter = useRef<(newValue: string) => void>();
	const formRef = useRef<HTMLFormElement>(null);
	
	
	// Handle going forward/backward
	const isValidStepNumber = (step: number): step is 0 | 1 | 2 | 3 | 4 | 5 => step >= 0 && step <= 5;
	
	const goForward = useCallback(() => {
		const nextStep = stepNumber + 1;
		const plausibleEvents: Record<Exclude<StepNumbers, 5>, string> = {
			0: 'Submitted email address',
			1: 'Validated code',
			2: 'Created user',
			3: 'Created website',
			4: 'Updated location',
		};
		
		if (!isValidStepNumber(nextStep) || stepNumber === 5) {
			throw new Error('`goForward` called on invalid step');
		}
		
		if (stepNumber === 2) {
			void memoizedLoadSelf();
		}
		
		if (stepNumber === 3) {
			window.plausible(plausibleEvents[stepNumber]);
			
			return Promise.all([memoizedLoadSelf(), memoizedLoadStart()]);
		}
		
		window.plausible(plausibleEvents[stepNumber]);
		
		setStepNumber(nextStep);
	}, [stepNumber, memoizedLoadStart, memoizedLoadSelf, setStepNumber]);
	
	const goBackward = useCallback(() => {
		const previousStep = stepNumber - 1;
		
		if (!isValidStepNumber(previousStep)) {
			throw new Error('`goBackward` called on invalid step');
		}
		
		setStepNumber(previousStep);
	}, [stepNumber, setStepNumber]);
	
	
	// Create fingerprint on mount
	useEffect(() => {
		void (async () => {
			try {
				setFingerprint(await getFingerprint());
			} catch (error: unknown) {
				// Report to Sentry
				Sentry.captureException(error);
			}
		})();
	}, []);
	
	
	// Build form options indexed by step number
	const formOptionsByStep = useMemo<APIFormOptions<unknown, unknown, unknown>[]>(
		() => [
			{
				action: 'GENERATE',
				uri: '/start/verification-code',
				submitButton: {
					text: 'Continue',
				},
				appearsOnBackground: false,
				recaptcha: {
					enabled: true,
					legalNotice: {
						disabled: true,
					},
				},
				...(migrate ? { extraParameters: { migrate } } : {}),
			},
			{
				action: 'VALIDATE',
				uri: '/start/verification-code',
				submitButton: {
					text: 'Continue',
				},
				appearsOnBackground: false,
				cancelButton: {
					text: 'Go back',
				},
				recaptcha: {
					enabled: true,
					legalNotice: {
						disabled: true,
					},
				},
				...(migrate ? { extraParameters: { migrate } } : {}),
			},
			{
				action: 'POST',
				uri: '/start/user',
				submitButton: {
					text: 'Continue',
				},
				appearsOnBackground: false,
				recaptcha: {
					enabled: true,
					legalNotice: {
						disabled: true,
					},
				},
				...(migrate ? { extraParameters: { migrate } } : {}),
			},
			{
				action: startState?.websiteID ? 'PUT' : 'POST',
				uri: `/start${migrate && !startState.websiteID ? '' : '/website'}${
					startState?.websiteID ? `/${startState.websiteID}` : ''
				}${migrate ? '/migration' : ''}`,
				submitButton: {
					text: 'Continue',
				},
				appearsOnBackground: false,
				recoverState: !startState?.websiteID,
				...(fingerprint && !startState?.websiteID ? { extraParameters: { adClickFingerprint: fingerprint } } : {}),
			},
			{
				action: 'POST',
				uri: `/start/website/${startState?.websiteID ?? ''}/location`,
				submitButton: {
					text: 'Continue',
				},
				appearsOnBackground: false,
				cancelButton: {
					text: 'Go back',
				},
				recoverState: false,
			},
		],
		[startState?.websiteID, migrate, fingerprint]
	);
	
	
	// Remove persisted data on finish step
	useEffect(() => {
		if (stepNumber === 5) {
			// Type the history state object
			const persistedStateSchema = z
				.object({
					state: z.object({
						persistentForm: z.record(z.record(z.unknown())),
					}),
				})
				.deepPartial()
				.nullable();
			
			const typedHistoryState = persistedStateSchema.parse(window.history.state);
			
			
			// Remove values persisted from the onboarding step forms
			if (typedHistoryState?.state?.persistentForm) {
				for (const { action, uri } of formOptionsByStep) {
					// This is the key the values would be persisted under for a given step
					let recoverableKey = `${action} ${uri}`;
					
					
					// The community step will now appear as a `PUT` since a community is currently being created, but we want to clear the values persisted before it was created
					if (recoverableKey.startsWith('PUT /start/website/')) {
						recoverableKey = `POST /start${migrate ? '/migration' : '/website'}`;
					}
					
					
					// Remove the value from the existing persisted forms
					const { [recoverableKey]: _ignore, ...everythingElse } = typedHistoryState.state.persistentForm;
					
					
					// Assign the remaining values back to the persisted forms
					typedHistoryState.state.persistentForm = everythingElse;
				}
				
				window.history.replaceState(typedHistoryState, '');
			}
		}
	}, [stepNumber, formOptionsByStep, migrate]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			{Boolean(formOptionsByStep[stepNumber]) && (
				<APIForm
					options={formOptionsByStep[stepNumber]}
					onCompletion={goForward}
					onCancel={stepNumber === 1 || stepNumber === 4 ? goBackward : undefined}
					ref={formRef}
				>
					{stepNumber < 3 && (
						<SignUp stepNumber={stepNumber} emailGetter={emailGetter} storybookEmail={storybookEmail} />
					)}
					
					{stepNumber > 0 && stepNumber < 3 && (
						<VerificationCode
							stepNumber={stepNumber}
							emailGetter={emailGetter}
							verificationCodeSetter={verificationCodeSetter}
							verificationCodeExpiredError={verificationCodeExpiredError}
							setVerificationCodeExpiredError={setVerificationCodeExpiredError}
							formRef={formRef}
						/>
					)}
					
					{stepNumber === 2 && <User />}
					
					{stepNumber === 3 && (
						<React.Fragment>
							{migrate ? (
								<MigrateCommunity
									startState={startState}
									storybookContainsProtectedContent={storybookContainsProtectedContent}
								/>
							) : (
								<NewCommunity startState={startState} storybookCommunityName={storybookCommunityName} />
							)}
							<SelectWrapper>
								<Select
									name='hearAbout'
									label='How’d you hear about HOA Express?'
									note='Optional'
									placeholder='Please select…'
									icon={faCommentQuestion}
									initialValue={startState.hearAbout}
								>
									<option>Search</option>
									<option>Referral</option>
									<option>Social media</option>
									<option>AI chatbot (ChatGPT, Gemini, etc.)</option>
									<option>Customer review site</option>
									<option>Another community’s website</option>
									<option>Mailed flyer</option>
									<option>Other</option>
								</Select>
							</SelectWrapper>
						</React.Fragment>
					)}
					
					{stepNumber === 4 && <Location />}
					
					<MultiStepErrorHandler
						stepNumber={stepNumber}
						setStepNumber={setStepNumber}
						startsWith={{}}
						fieldToStepNumberMap={{ email: 0, verificationCode: 1 }}
						verificationCodeSetter={verificationCodeSetter}
						setVerificationCodeExpiredError={setVerificationCodeExpiredError}
					/>
				</APIForm>
			)}
			
			{stepNumber === 5 && (
				<Finish
					startState={startState}
					migrate={migrate}
					storybookOnboardingCallScheduled={storybookOnboardingCallScheduled}
					storybookLoadingCompletion={storybookLoadingCompletion}
					storybookCompleted={storybookCompleted}
				/>
			)}
			
			{stepNumber === 0 && (
				<Subtext>
					By continuing, you agree to our {/* eslint-disable-next-line react/jsx-no-target-blank */}
					<a
						href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/policies/terms-of-service`}
						target='_blank'
					>
						Terms of Service
					</a>{' '}
					and {/* eslint-disable-next-line react/jsx-no-target-blank */}
					<a
						href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/policies/privacy-policy`}
						target='_blank'
					>
						Privacy Policy
					</a>
				</Subtext>
			)}
			
			{stepNumber === 3 &&
				(migrate
					? !startState.migration && (
							<Subtext>
								Changed your mind? <a href='/'>Start from scratch</a> instead.
							</Subtext>
						)
					: startState.onboardStep < 102 && (
							<Subtext>
								Have an existing website? We’ll <a href='/?migrate=true'>migrate it for free</a>!
							</Subtext>
						))}
		</React.Fragment>
	);
};

export default Steps;
